import '@ionic/react/css/core.css'

import { Capacitor } from '@capacitor/core'
import { IonContent, IonModal, ModalOptions } from '@ionic/react'
import { GlobalStyles, useTheme } from '@mui/material'
import { forwardRef, PropsWithChildren, useEffect } from 'react'
import { isIOS } from 'react-device-detect'

export type BottomSheetProps = PropsWithChildren<{
  open: boolean
  animated?: boolean
  onClose: () => void
  breakpoints: ModalOptions['breakpoints']
  initialBreakpoint: ModalOptions['initialBreakpoint']
  handle?: boolean
  backdropDismiss?: boolean
  swipeToClose?: boolean
  canDismiss?: boolean
  responsive?: boolean
}>

export const BottomSheet = forwardRef<HTMLIonModalElement, BottomSheetProps>(
  (
    {
      open,
      onClose,
      animated,
      breakpoints,
      initialBreakpoint,
      handle,
      backdropDismiss = true,
      swipeToClose,
      canDismiss,
      children,
      responsive = false,
    },
    ref
  ) => {
    const theme = useTheme()
    const isNative = Capacitor.isNativePlatform()

    const styles = {
      'ion-content': {
        '--overflow': 'scroll',
        '--background': responsive
          ? theme.palette?.background?.default
          : undefined,
      },
      'ion-content::part(scroll)': {
        backgroundColor: theme.palette?.background?.default,
        width: responsive ? '100%' : undefined,
        margin: responsive ? '0 auto' : undefined,
      },
      'ion-modal::part(content)': {
        maxWidth: responsive ? '100%' : 600,
      },
      'ion-modal.parent-modal::part(content)': {
        // IonModalの背景を広げる
        // IonModalから更にダイアログ等を出した時に子の背景も広がってしまうため、第一階層のIonModalにのみ限定する
        width: responsive ? '100%' : undefined,
      },
      'ion-modal': {
        position: 'fixed',
      } as never,

      /**
       * @hack
       * 見た目はそのまま
       * アクションシートのスワイプ可能領域サイズを大きくする
       */
      '::part(handle)': {
        opacity: handle ? '1' : 0,
        backgroundColor: 'initial',
        // content（背景）の幅を広げるとhandleの領域も広くなりクローズボタンの操作を阻害するためmaxWidthは600px基準とする
        maxWidth: responsive ? 'calc(600px - 92px)' : undefined,
        width: 'calc(100% - 92px)',
        marginTop: 8,
        height: 30,
        outline: 0,
        WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
        '&::before': {
          background: 'var(--ion-color-step-350, #d0d0ce)',
          borderRadius: 8,
          height: 0,
          padding: '2.7px 3.3px',
        },
      },
    }

    useEffect(() => {
      if (!document) return
      if (open) {
        document.body.style.overflowY = 'hidden'
        return
      }
      if (isIOS && isNative) {
        document.body.style.overflowY = 'unset'
        return
      }
      document.body.style.overflowY = 'auto'

      return () => {
        document.body.style.overflowY = 'auto'
      }
    }, [open])

    return (
      <>
        <GlobalStyles styles={styles} />
        <IonModal
          className="parent-modal"
          ref={ref}
          isOpen={open}
          mode="ios"
          animated={animated}
          onDidDismiss={onClose}
          breakpoints={breakpoints}
          initialBreakpoint={initialBreakpoint}
          handle={handle}
          swipeToClose={swipeToClose}
          canDismiss={canDismiss}
          backdropDismiss={backdropDismiss}
        >
          <IonContent>{children}</IonContent>
        </IonModal>
      </>
    )
  }
)
